import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { DEFAULT_LOCALE, localeFromPath, localizePath, translatePath } from './routes';

export const LOCALE_STORAGE_KEY = 'lpd.locale';

const LocaleContext = createContext(null);

export function formatPriceFor(locale, value) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '';
  const amount = Number.isInteger(value) ? String(value) : value.toFixed(2);
  return locale === 'en' ? `€${amount}` : `${amount} €`;
}

// Montants du panier et du paiement : toujours deux décimales (30.00 € / €30.00).
export function formatMoneyFor(locale, value) {
  const amount = (Number(value) || 0).toFixed(2);
  return locale === 'en' ? `€${amount}` : `${amount} €`;
}

export function formatDateFor(locale, value, options = { day: 'numeric', month: 'long', year: 'numeric' }) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(locale === 'en' ? 'en-GB' : 'fr-FR', options);
}

function buildValue(locale) {
  return {
    locale,
    isEnglish: locale === 'en',
    // Lien écrit en français → adresse dans la langue courante.
    lp: path => localizePath(path, locale),
    // Choisit la variante de texte : pick({ fr: '…', en: '…' }).
    pick: variants => (variants && (variants[locale] ?? variants[DEFAULT_LOCALE])),
    price: value => formatPriceFor(locale, value),
    money: value => formatMoneyFor(locale, value),
    date: (value, options) => formatDateFor(locale, value, options),
    switchPath: (path, target) => translatePath(path, target, { fallback: 'home' }),
  };
}

export function LocaleProvider({ children }) {
  const { pathname } = useLocation();
  const locale = localeFromPath(pathname);
  const value = useMemo(() => buildValue(locale), [locale]);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

// Hors provider (tests unitaires d'un composant isolé), on retombe sur le français.
export function useLocale() {
  return useContext(LocaleContext) || buildValue(DEFAULT_LOCALE);
}

// Textes d'un composant regroupés par langue : const c = useCopy(COPY).
export function useCopy(copy) {
  const { locale } = useLocale();
  return copy[locale] || copy[DEFAULT_LOCALE];
}

export function rememberLocale(locale) {
  try { localStorage.setItem(LOCALE_STORAGE_KEY, locale); } catch { }
}
